import React, { useState, useEffect, useMemo } from 'react';
import { useOutletContext, useNavigate } from 'react-router-dom';
import { BookOpen, Lock, ArrowRight, AlertTriangle } from 'lucide-react';
import { PageHeader } from '../../components/common/PageHeader';
import { SkeletonTablePage } from '../../components/common/SkeletonLoader';
import { db, functions } from '../../lib/firebase';
import { doc, getDoc, documentId, collection, query, where, getDocs } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { useSubscriptionTier } from '../../hooks/useSubscriptionTier';
import type { ResolvedLoanBook } from '@valutten/shared-schemas';
import { resolveLoanBookSelection } from '../../utils/loanBookResolution';
import { resolveTemplateDisplayName } from '../../utils/templateDisplayName';
import { SubscriptionTier, getTierDisplayName } from '../../config/subscriptionTiers';

// Tier limits (mirrors TIER_DEFINITIONS SSOT in shared-schemas)
const TIER_TEMPLATE_LIMITS: Record<string, number> = {
  plus: 1,
  pro: 3,
  enterprise: -1, // unlimited
};

// Next tier for upgrade CTA
const NEXT_TIER: Partial<Record<SubscriptionTier, SubscriptionTier>> = {
  plus: 'pro',
  pro: 'enterprise',
};

const LoanBooksPage = () => {
    const { userData, companyData, loanBooks = [] } = useOutletContext<{ userData: any; companyData: any; loanBooks?: ResolvedLoanBook[] }>();
    const navigate = useNavigate();
    const { tier } = useSubscriptionTier();
    const [templates, setTemplates] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [updatingId, setUpdatingId] = useState<string | null>(null);

    const templateLimit = TIER_TEMPLATE_LIMITS[tier] ?? -1;
    const enabledIds: string[] = companyData?.enabledTemplateIds || [];
    const enabledCount = enabledIds.length;
    const isUnlimited = templateLimit === -1;
    const atLimit = !isUnlimited && enabledCount >= templateLimit;
    const overLimit = !isUnlimited && enabledCount > templateLimit;

    useEffect(() => {
        const fetchTemplates = async () => {
            if (!userData) return;
            setLoading(true);
            // Clear any banner from a previous attempt: this effect re-runs when the
            // company's template ids change, and a stale error would outlive its cause.
            setError('');
            try {
                // Read ENABLED and ASSIGNED template ids by documentId(), as
                // AppLayout.tsx does. The previous fallback branch ran an unfiltered
                // `where('isActive','==',true)` collection scan whenever
                // assignedTemplateIds was absent. Under
                // featureFlags/firestore_rules.fileTemplates_strict_read (PROD-live
                // 2026-04-24) that scan is CROSS-TENANT for a super_admin, who
                // bypasses the rule and so read every active template on the
                // platform, and permission-denied for an administrator, who may read
                // only ids in enabledTemplateIds. Both are wrong; neither is fixed by
                // filtering client-side, because rules are not filters.
                //
                // The two sets are fetched SEPARATELY and the assigned-only set fails
                // SOFT: an administrator cannot read an assigned-but-not-yet-enabled
                // template, and with a single Promise.all one denied chunk would
                // reject the whole load, including the enabled books they can read.
                // Enabled ids are included so a tenant whose assignedTemplateIds was
                // never backfilled still sees the loan books it actually has.
                const enabledIdList: string[] = companyData?.enabledTemplateIds || [];
                const assignedIdList: string[] = companyData?.assignedTemplateIds || [];
                const enabledSet = new Set<string>(enabledIdList);
                const assignedOnly = Array.from(
                    new Set<string>(assignedIdList.filter(id => !enabledSet.has(id)))
                );
                const enabledOnly = Array.from(enabledSet);

                if (enabledOnly.length === 0 && assignedOnly.length === 0) {
                    setTemplates([]);
                    return;
                }

                // Firestore `in` caps at 30 ids per query.
                const chunk = (ids: string[]): string[][] => {
                    const out: string[][] = [];
                    for (let i = 0; i < ids.length; i += 30) out.push(ids.slice(i, i + 30));
                    return out;
                };
                const readChunk = (batch: string[]) =>
                    getDocs(query(collection(db, 'fileTemplates'), where(documentId(), 'in', batch)));

                const [enabledSnaps, assignedSnaps] = await Promise.all([
                    // The caller may always read its enabled books; a failure here is real.
                    Promise.all(chunk(enabledOnly).map(readChunk)),
                    // Assigned-but-not-enabled is unreadable for an administrator under
                    // the strict-read rule. Losing those must not lose the enabled ones.
                    Promise.all(
                        chunk(assignedOnly).map(batch =>
                            readChunk(batch).catch(err => {
                                console.warn('[LoanBooksPage] assigned-only template chunk unreadable', err);
                                return { docs: [] as any[] };
                            })
                        )
                    ),
                ]);
                const fetched = [...enabledSnaps, ...assignedSnaps]
                    .flatMap(snap => snap.docs.map((d): Record<string, any> => ({ id: d.id, ...d.data() })))
                    .filter(t => t.isActive !== false);
                setTemplates(
                    fetched.sort((a, b) =>
                        resolveTemplateDisplayName(a).localeCompare(resolveTemplateDisplayName(b))
                    )
                );
            } catch (err: any) {
                console.error('[LoanBooksPage] fetchTemplates failed', err);
                setError('Could not load loan books. Please try again or contact support.');
            } finally {
                setLoading(false);
            }
        };

        fetchTemplates();
    }, [userData, companyData?.assignedTemplateIds, companyData?.enabledTemplateIds]);

    const handleToggle = async (templateId: string, isEnabled: boolean) => {
        if (!companyData?.id) {
            setError('Company data is not available.');
            return;
        }
        setUpdatingId(templateId);
        setError('');
        try {
            const toggleFn = httpsCallable(functions, 'toggleLoanBook');
            await toggleFn({
                templateId,
                action: isEnabled ? 'disable' : 'enable',
            });
        } catch (err: any) {
            if (err.code === 'functions/resource-exhausted') {
                const nextTier = NEXT_TIER[tier];
                setError(
                    nextTier
                        ? `You've reached your ${getTierDisplayName(tier)} plan limit of ${templateLimit} loan book${templateLimit === 1 ? '' : 's'}. Upgrade to ${getTierDisplayName(nextTier)} for more.`
                        : 'You have reached the maximum number of loan books for your plan.'
                );
            } else {
                setError(`Failed to update loan book: ${err.message}`);
            }
        } finally {
            setUpdatingId(null);
        }
    };

    // Split templates into enabled and available
    const { enabledTemplates, availableTemplates } = useMemo(() => {
        const enabled: any[] = [];
        const available: any[] = [];
        templates.forEach(t => {
            if (enabledIds.includes(t.id)) {
                enabled.push(t);
            } else {
                available.push(t);
            }
        });
        return { enabledTemplates: enabled, availableTemplates: available };
    }, [templates, enabledIds]);

    // Usage badge color
    const usageBadgeColor = useMemo(() => {
        if (isUnlimited) return 'text-amber-400';
        const ratio = enabledCount / templateLimit;
        if (ratio >= 1) return 'text-red-400';
        if (ratio >= 0.8) return 'text-yellow-400';
        return 'text-cyan-400';
    }, [enabledCount, templateLimit, isUnlimited]);

    const isSuperAdmin = userData?.role === 'super_admin';
    const nextTier = NEXT_TIER[tier];

    if (loading) {
        return <SkeletonTablePage tableColumns={3} />;
    }

    return (
        <div className="space-y-6">
            <PageHeader
                icon={BookOpen}
                title="Loan Books"
                subtitle="Manage which loan books are active for your company."
                actions={
                    <div className={`text-sm font-medium ${usageBadgeColor}`}>
                        {isUnlimited
                            ? 'Unlimited'
                            : `${enabledCount}/${templateLimit} enabled`
                        }
                        <span className="text-gray-500 ml-1">({getTierDisplayName(tier)})</span>
                    </div>
                }
            />

            {error && (
                <div className="bg-red-900/50 border border-red-700 text-red-300 p-3 rounded-lg text-sm">
                    {error}
                </div>
            )}

            {/* Over-limit warning (downgrade scenario) */}
            {overLimit && (
                <div className="bg-amber-900/30 border border-amber-700/50 rounded-lg p-4 flex items-start gap-3">
                    <AlertTriangle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
                    <div>
                        <p className="text-amber-200 text-sm font-medium">
                            You have more loan books enabled ({enabledCount}) than your {getTierDisplayName(tier)} plan allows ({templateLimit}).
                        </p>
                        <p className="text-amber-300/70 text-sm mt-1">
                            Your existing loan books will continue to work, but you cannot enable new ones.
                            Disable loan books you no longer need, or upgrade your plan.
                        </p>
                        {isSuperAdmin && nextTier && (
                            <button
                                onClick={() => navigate('/settings/billing')}
                                className="mt-3 inline-flex items-center gap-1.5 text-sm font-medium text-amber-400 hover:text-amber-300 transition-colors"
                            >
                                Upgrade to {getTierDisplayName(nextTier)}
                                <ArrowRight className="w-4 h-4" />
                            </button>
                        )}
                    </div>
                </div>
            )}

            {/* Enabled Loan Books */}
            {enabledTemplates.length > 0 && (
                <div>
                    <h3 className="text-sm font-medium text-gray-400 uppercase tracking-wider mb-3">
                        Your Enabled Loan Books
                    </h3>
                    <div className="bg-gray-800 rounded-lg shadow-lg overflow-hidden">
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 p-6">
                            {enabledTemplates.map((template: any) => {
                                const isUpdating = updatingId === template.id;
                                return (
                                    <div
                                        key={template.id}
                                        className="bg-gray-700 rounded-lg p-6 flex flex-col justify-between shadow-md ring-2 ring-blue-500"
                                    >
                                        <div>
                                            <h3 className="text-lg font-bold text-white">{resolveTemplateDisplayName(template)}</h3>
                                            <p className="text-sm text-gray-400 mt-1">Enabled for your company</p>
                                        </div>
                                        <button
                                            onClick={() => handleToggle(template.id, true)}
                                            disabled={isUpdating}
                                            className="w-full mt-6 px-4 py-2 rounded-md text-sm font-semibold transition-colors disabled:opacity-50 disabled:cursor-wait bg-green-600 hover:bg-red-700 text-white"
                                        >
                                            {isUpdating ? 'Updating...' : 'Disable'}
                                        </button>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>
            )}

            {/* Available Loan Books */}
            {availableTemplates.length > 0 && (
                <div>
                    <h3 className="text-sm font-medium text-gray-400 uppercase tracking-wider mb-3">
                        Available Loan Books
                    </h3>
                    <div className="bg-gray-800 rounded-lg shadow-lg overflow-hidden">
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 p-6">
                            {availableTemplates.map((template: any) => {
                                const isUpdating = updatingId === template.id;
                                const isLocked = atLimit || overLimit;

                                return (
                                    <div
                                        key={template.id}
                                        className={`bg-gray-700 rounded-lg p-6 flex flex-col justify-between shadow-md transition-all ${
                                            isLocked ? 'opacity-60' : ''
                                        }`}
                                    >
                                        <div>
                                            <div className="flex items-start justify-between">
                                                <h3 className="text-lg font-bold text-white">{resolveTemplateDisplayName(template)}</h3>
                                                {isLocked && (
                                                    <Lock className="w-4 h-4 text-gray-500 flex-shrink-0 mt-1" />
                                                )}
                                            </div>
                                            <p className="text-sm text-gray-400 mt-1">
                                                {isLocked
                                                    ? `Upgrade to enable more loan books`
                                                    : 'Available to enable'
                                                }
                                            </p>
                                        </div>

                                        {isLocked ? (
                                            nextTier ? (
                                                isSuperAdmin ? (
                                                    <button
                                                        onClick={() => navigate('/settings/billing')}
                                                        className="w-full mt-6 px-4 py-2 rounded-md text-sm font-semibold transition-colors bg-gradient-to-r from-purple-500 to-pink-500 hover:opacity-90 text-white flex items-center justify-center gap-2"
                                                    >
                                                        Upgrade to {getTierDisplayName(nextTier)}
                                                        <ArrowRight className="w-4 h-4" />
                                                    </button>
                                                ) : (
                                                    <div className="w-full mt-6 px-4 py-2 rounded-md text-sm font-medium text-gray-500 bg-gray-600/50 text-center">
                                                        Contact your admin to upgrade
                                                    </div>
                                                )
                                            ) : (
                                                <div className="w-full mt-6 px-4 py-2 rounded-md text-sm font-medium text-gray-500 bg-gray-600/50 text-center">
                                                    Plan limit reached
                                                </div>
                                            )
                                        ) : (
                                            <button
                                                onClick={() => handleToggle(template.id, false)}
                                                disabled={isUpdating}
                                                className="w-full mt-6 px-4 py-2 rounded-md text-sm font-semibold transition-colors disabled:opacity-50 disabled:cursor-wait bg-blue-600 hover:bg-blue-700 text-white"
                                            >
                                                {isUpdating ? 'Updating...' : 'Enable'}
                                            </button>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>
            )}

            {/* Empty state */}
            {templates.length === 0 && !loading && (
                <div className="bg-gray-800 rounded-lg p-12 text-center">
                    <BookOpen className="w-12 h-12 text-gray-600 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-gray-300">No loan books assigned</h3>
                    <p className="text-sm text-gray-500 mt-2">
                        Loan books are assigned during onboarding. Contact support if you need templates added.
                    </p>
                </div>
            )}
        </div>
    );
};

export default LoanBooksPage;
